'use client';

import { useTranslations } from 'next-intl';
import { useContext } from 'react';

import { Checkbox } from '@hrworks/sui-core/Checkbox';
import { DateField } from '@hrworks/sui-core/DateField';
import { GridItem } from '@hrworks/sui-core/GridItem';
import { NumberInputField } from '@hrworks/sui-core/NumberInputField';
import { Textarea } from '@hrworks/sui-core/Textarea';
import { TextField } from '@hrworks/sui-core/TextField';
import { CurrencySelect } from '@hrworks/sui-extension/CurrencySelect';

import { PhoneField } from '../../PhoneField';
import { ApplicationFormContext } from '../ApplicationFormContext';
import { CountrySelect } from '../CountrySelect';
import { getFieldProps } from '../getFieldProps';
import { S } from './FormField.styles';
import type { FormFieldProps } from './FormField.types';

export const FormField = ({
  size = 6,
  fieldKey,
  component = 'TextField',
  options,
  visibility,
  label: labelOverride,
  helpText,
  defaultValue: defaultValueOverride,
  maxValue,
  type,
  inputMode,
  min,
  max,
  signed,
}: FormFieldProps) => {
  const t = useTranslations('FormFields');
  const { formState } = useContext(ApplicationFormContext);

  if (visibility === 'NotVisible' || visibility === 'Administrator') return null;

  const mandatory = visibility === 'Mandatory';
  const labelPlacement = 'inside' as const;
  const fieldProps = getFieldProps(formState, fieldKey, t);
  const commonProps = {
    labelPlacement,
    mandatory,
    ...fieldProps,
    ...(labelOverride && { label: labelOverride, 'aria-label': labelOverride }),
    ...(helpText && { helpText }),
    ...(defaultValueOverride && { defaultValue: defaultValueOverride }),
    ...(maxValue && { maxValue }),
    placeholder: '',
  };
  // Checkboxen brauchen defaultChecked statt defaultValue – defaultValue landet sonst als
  // value-Attribut am Input und der Haken geht beim Re-Render nach einem Serverfehler verloren.
  const { defaultValue: _defaultValue, ...checkboxProps } = commonProps;
  const componentMap = {
    Checkbox: <Checkbox {...checkboxProps} defaultChecked={formState.inputs?.[fieldKey] === 'on'} />,
    Textarea: <Textarea {...commonProps} labelPlacement="floating" />,
    TextField: <TextField {...commonProps} {...(type && { type })} {...(inputMode && { inputMode })} />,
    NumberInputField: (
      <NumberInputField
        {...commonProps}
        {...(type && { type })}
        {...(inputMode && { inputMode })}
        min={min}
        max={max}
        signed={signed}
      />
    ),
    DateField: <DateField {...commonProps} />,
    PhoneField: <PhoneField {...commonProps} />,
    CurrencySelect: <CurrencySelect {...commonProps} />,
    CountrySelect: <CountrySelect {...commonProps} />,
    Select: <S.Select options={options || []} {...commonProps} />,
  };

  return (
    <GridItem data-guide-id={`form-field-${fieldKey}`} size={size}>
      {componentMap[component]}
    </GridItem>
  );
};
